// Tuning values: one value per knob (knobs.ts), a number or a colour by the knob's kind. values.json is the committed
// set; the tuner saves the same shape. What each kind accepts, how it is stored and shown lives in KIND_RULES, so a new
// kind of knob is one entry there.

import { parseColourHex, type ColourHex } from './colour'
import { TuningError } from './errors'
import { KNOB_IDS, isKnobId, knobDef, type KnobDef, type KnobId, type KnobKind, type NumberKnobDef, type KNOBS } from './knobs'

/** The value type of each kind of knob. */
export interface KnobValueOfKind {
  readonly number: number
  readonly colour: ColourHex
}

export type KnobValue<K extends KnobId> = KnobValueOfKind[(typeof KNOBS)[K]['kind']]
/** Any knob's value. */
export type AnyKnobValue = KnobValueOfKind[KnobKind]

export type TuningValues = { readonly [K in KnobId]: KnobValue<K> }

/** The committed values file, relative to the project root (tax-globe-war/). */
export const TUNING_VALUES_PATH = 'packages/tuning/values.json'

/** Stored numbers stay this precise: more decimals only record slider float noise. */
const STORED_SIGNIFICANT_DIGITS = 6

interface KindRules<D extends KnobDef, V> {
  /** What a value of this kind looks like, for problem messages. */
  readonly shape: string
  /** The value, if it has this kind's shape (a colour comes back normalised), else null. Ranges are not checked. */
  readonly read: (value: unknown) => V | null
  /** Why a well-shaped value is outside knob `def`'s range, or null. */
  readonly outOfRange: (def: D, value: V) => string | null
  /** The value as stored and published: numbers rounded to STORED_SIGNIFICANT_DIGITS. */
  readonly stored: (value: V) => V
  readonly format: (value: V) => string
}

type KindRulesTable = { readonly [K in KnobKind]: KindRules<Extract<KnobDef, { readonly kind: K }>, KnobValueOfKind[K]> }

const storedNumber = (value: number): number => Number(value.toPrecision(STORED_SIGNIFICANT_DIGITS))

const KIND_RULES: KindRulesTable = {
  number: {
    shape: 'a finite number',
    read: (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null),
    outOfRange: (def: NumberKnobDef, value) => (value < def.min || value > def.max ? `${value} is outside ${def.min}…${def.max}` : null),
    stored: storedNumber,
    format: (value) => String(storedNumber(value)),
  },
  colour: {
    shape: 'a colour #RRGGBB',
    read: (value) => (typeof value === 'string' ? parseColourHex(value) : null),
    outOfRange: () => null,
    stored: (value) => value,
    format: (value) => value,
  },
}

/** The rules of knob `id`'s kind, typed for any knob (the table is keyed by kind; TypeScript cannot correlate the two). */
function rulesOf(id: KnobId): { readonly def: KnobDef; readonly rules: KindRules<KnobDef, AnyKnobValue> } {
  const def = knobDef(id)
  return { def, rules: KIND_RULES[def.kind] as unknown as KindRules<KnobDef, AnyKnobValue> }
}

export function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export type KnobReading<K extends KnobId> = { readonly kind: 'ok'; readonly value: KnobValue<K> } | { readonly kind: 'problem'; readonly problem: string }

/** Strict reading of one value for knob `id`: its kind's shape and within the knob's range. */
export function readKnobValue<K extends KnobId>(id: K, value: unknown): KnobReading<K> {
  const { def, rules } = rulesOf(id)
  const read = rules.read(value)
  if (read === null) return { kind: 'problem', problem: `${id} must be ${rules.shape}` }
  const outOfRange = rules.outOfRange(def, read)
  if (outOfRange !== null) return { kind: 'problem', problem: `${id} = ${outOfRange}` }
  return { kind: 'ok', value: rules.stored(read) as KnobValue<K> }
}

/** Why `value` cannot be knob `id`'s value, or null when it can. */
export function knobProblem(id: KnobId, value: unknown): string | null {
  const reading = readKnobValue(id, value)
  return reading.kind === 'ok' ? null : reading.problem
}

/**
 * Lenient reading for a published document (a live site's /tuning.json): the kind's shape only, so a value outside
 * today's range still shows as it is. Null when the value does not even have the shape.
 */
export function readPublishedValue<K extends KnobId>(id: K, value: unknown): KnobValue<K> | null {
  return rulesOf(id).rules.read(value) as KnobValue<K> | null
}

/** The shape a knob's value must have, for messages. */
export function knobShape(id: KnobId): string {
  return rulesOf(id).rules.shape
}

/** A value as stored: a slider's 0.30000000000000004 is stored as 0.3; colours are stored as read. */
export function storedValue<K extends KnobId>(id: K, value: KnobValue<K>): KnobValue<K> {
  return rulesOf(id).rules.stored(value) as KnobValue<K>
}

/** A value as the tuner and commit messages show it. */
export function formatKnobValue<K extends KnobId>(id: K, value: KnobValue<K>): string {
  return rulesOf(id).rules.format(value)
}

/** Knob `id`'s value, typed by its kind (TypeScript does not narrow TuningValues[K] for a generic K by itself). */
export function knobValue<K extends KnobId>(values: TuningValues, id: K): KnobValue<K> {
  return values[id] as unknown as KnobValue<K>
}

/** `values` with knob `id` set to `value` (a copy: values are never mutated). */
export function withKnobValue<K extends KnobId>(values: TuningValues, id: K, value: KnobValue<K>): TuningValues {
  return { ...values, [id]: value }
}

/**
 * Strict reading of a values document (values.json, or a save from the tuner): every knob, each of its kind and within
 * its range, and nothing else. `source` names the document in the error.
 */
export function parseTuning(json: unknown, source: string): TuningValues {
  if (!isRecord(json)) throw new TuningError(`${source}: tuning values must be a JSON object`)
  const problems: string[] = []
  const values = {} as Record<KnobId, AnyKnobValue>
  for (const id of KNOB_IDS) {
    const reading = readKnobValue(id, json[id])
    if (reading.kind === 'ok') values[id] = reading.value
    else problems.push(reading.problem)
  }
  for (const key of Object.keys(json)) if (!isKnobId(key)) problems.push(`${key} is not a knob`)
  if (problems.length > 0) throw new TuningError(`${source}: ${problems.join('; ')}`)
  return values as TuningValues
}

/** The values in knob-table order, as stored. */
export function orderedValues(values: TuningValues): Record<KnobId, AnyKnobValue> {
  const ordered = {} as Record<KnobId, AnyKnobValue>
  for (const id of KNOB_IDS) ordered[id] = storedValue(id, values[id])
  return ordered
}

/** The values document as committed: knob-table order, two-space indent, one trailing newline (stable diffs). */
export function serializeTuning(values: TuningValues): string {
  return `${JSON.stringify(orderedValues(values), null, 2)}\n`
}

/** Knobs whose values differ, in table order. */
export function changedKnobs(from: TuningValues, to: TuningValues): KnobId[] {
  return KNOB_IDS.filter((id) => from[id] !== to[id])
}
